import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, catchError, debounceTime, distinctUntilChanged, filter, map, of, skip, switchMap, tap } from 'rxjs';

import { FilterService, FilterServiceActions } from '@ninety/platform/services';
import { FilterUserSelectors, LayoutsIssuesEvents, TeamSelectors, UserListStateActions } from '@ninety/platform/state';

import { IssuesV2Service } from '../../_shared/services/issues-v2.service';
import { IssuesActions } from '../issues.actions';

import { IssuesArchiveApiActions, IssuesArchivePageActions } from './archive.actions';
import { IssueArchiveRow } from './archive.model';
import { selectIssuesArchiveQuery, selectShowIssuesArchiveTab } from './archive.selectors';

@Injectable()
export class IssuesArchiveEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly issuesService = inject(IssuesV2Service);
  private readonly filterService = inject(FilterService);

  /**
   * The toolbar team selector isn't archive-aware, so re-query when the filter-bar team changes.
   * These effects stay subscribed for the app's lifetime once registered, so the `showArchiveTab`
   * gate keeps them from firing while the tab is closed. `skip(1)` drops the emission present on
   * subscription (often `null` during bootstrap); a team changed while the tab was closed is
   * covered by the `opened` reload.
   */
  teamChanged$ = createEffect(() =>
    this.store.select(TeamSelectors.selectFilterBarTeamId).pipe(
      skip(1),
      filter(Boolean),
      distinctUntilChanged(),
      concatLatestFrom(() => this.store.select(selectShowIssuesArchiveTab)),
      filter(([, showArchiveTab]) => showArchiveTab),
      map(() => IssuesArchivePageActions.teamChanged())
    )
  );

  /** The toolbar owner filter publishes through `UserListStateActions.changeFilterBarUsers`. */
  usersChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListStateActions.changeFilterBarUsers),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesArchiveTab),
        this.store.select(FilterUserSelectors.selectFilterBarUsers),
      ]),
      filter(([, showArchiveTab, users]) => showArchiveTab && !!users?.length),
      map(() => IssuesArchivePageActions.usersChanged())
    )
  );

  /**
   * The toolbar search box publishes through `FilterService.searchText$`; debounce typing and
   * re-query. Emissions already reflected in state are skipped — entering the tab clears the box
   * while the reducer resets `searchText`, which would otherwise re-query immediately.
   */
  searchChanged$ = createEffect(() =>
    this.filterService.searchText$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      skip(1),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesArchiveTab),
        this.store.select(selectIssuesArchiveQuery),
      ]),
      filter(([searchText, showArchiveTab, query]) => showArchiveTab && (searchText ?? '') !== query.searchText),
      map(([searchText]) => IssuesArchivePageActions.searchChanged({ searchText: searchText ?? '' }))
    )
  );

  /**
   * The search box is shared with the live lists, so entering or leaving the Archive tab starts a
   * fresh search context. (The reducer already resets the slice's searchText.)
   */
  clearSearchOnArchiveToggle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssuesArchivePageActions.opened, IssuesArchivePageActions.closed),
        tap(() => this.filterService.setSearchText(''))
      ),
    { dispatch: false }
  );

  /**
   * The Type filter pill lives in the shared filters toolbar (web-pages-layouts), which can't
   * depend on this lib — so it publishes a bridge event that we translate into the page action.
   * The reducer resets paging; loadArchive$ re-queries.
   */
  typeFilterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsIssuesEvents.archiveTypeFilterChanged),
      concatLatestFrom(() => this.store.select(selectShowIssuesArchiveTab)),
      filter(([, showArchiveTab]) => showArchiveTab),
      map(([{ intervalCode }]) => IssuesArchivePageActions.typeFilterChanged({ intervalCode }))
    )
  );

  /** The toolbar Refresh button publishes for every page; gate on the tab being open. */
  refreshClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.refreshed),
      concatLatestFrom(() => this.store.select(selectShowIssuesArchiveTab)),
      filter(([, showArchiveTab]) => showArchiveTab),
      map(() => IssuesArchivePageActions.refreshed())
    )
  );

  /**
   * Row click opens the archived issue in the detail card. The row is already a full issue in the
   * shared entities dict (the archive load uses the same adapter), so reusing the live open path
   * resolves it from state with no extra fetch.
   */
  openDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesArchivePageActions.detailOpened),
      map(({ issue }) => IssuesActions.openInDetailViewRequested({ issue }))
    )
  );

  /**
   * Unarchive reuses the live action, which already removes the row optimistically and decrements
   * `totalCount` in the reducer — exactly the behaviour the archive list wants, since an
   * unarchived issue no longer belongs here.
   */
  unarchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesArchivePageActions.unarchiveRequested),
      filter(({ issue }) => !!issue._id),
      map(({ issue }) => IssuesActions.unarchiveIssue({ issueId: issue._id }))
    )
  );

  loadArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesArchivePageActions.opened,
        IssuesArchivePageActions.paginationChanged,
        IssuesArchivePageActions.sortChanged,
        IssuesArchivePageActions.typeFilterChanged,
        IssuesArchivePageActions.teamChanged,
        IssuesArchivePageActions.usersChanged,
        IssuesArchivePageActions.searchChanged,
        IssuesArchivePageActions.refreshed,
        IssuesArchivePageActions.retryRequested
      ),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectIssuesArchiveQuery),
      ]),
      switchMap(([, teamId, { pageIndex, searchText, ...queryParams }]) => {
        // No team yet (bootstrap, or a deep link that beat the team list) is not a failed query —
        // the live list skips the same way, and `teamChanged$` re-runs this once a team lands.
        // Emitting nothing holds `Loading`, so the skeleton stays up instead of flashing an error.
        if (!teamId) return EMPTY;

        // `selectIssuesArchiveQuery` already omits intervalCode (for the All type filter) and
        // userIds (for the All-teams / empty owner filter), so spreading its remaining params is
        // enough — the include/omit rule lives in one place, not two. Unlike Trash, Archive lets
        // teamId 'all' pass straight through: the issues API resolves it to the caller's teams and
        // hydrates teamName for the Team column.
        return this.issuesService
          .searchTeamIssues({
            teamId,
            archived: true,
            page: pageIndex,
            searchText: searchText || undefined,
            ...queryParams,
          })
          .pipe(
            map(response =>
              IssuesArchiveApiActions.loadArchiveSuccess({
                rows: response.items as IssueArchiveRow[],
                totalCount: response.totalCount,
              })
            ),
            catchError(() => of(IssuesArchiveApiActions.loadArchiveFailure()))
          );
      })
    )
  );
}
